/**
 * AdvancedAnalyticsDashboard - Comprehensive analytics and ROI tracking
 * ZAPHUB 2.0 - Performance metrics, cost analysis, predictions
 */

import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Clock,
  Zap,
  AlertCircle,
  CheckCircle,
  Activity,
  BarChart3,
  PieChart as PieChartIcon,
  Download,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import type { WorkflowAnalytics, SystemAnalytics } from '@shared/types';

export const AdvancedAnalyticsDashboard: React.FC = () => {
  const [period, setPeriod] = useState<'day' | 'week' | 'month' | 'year'>('month');
  const [loading, setLoading] = useState(false);
  const [systemAnalytics, setSystemAnalytics] = useState<SystemAnalytics | null>(null);

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      // Mock data - in production, call API
      const mockAnalytics: SystemAnalytics = {
        period,
        workflows: {
          total: 150,
          active: 120,
          draft: 30,
        },
        executions: {
          total: 50000,
          successful: 47500,
          failed: 2500,
        },
        users: {
          total: 50,
          active: 45,
          newThisPeriod: 5,
        },
        costs: {
          total: 5000,
          byWorkflow: {
            'Email Campaign': 1500,
            'Lead Qualification': 1200,
            'Invoice Generator': 800,
            'Support Tickets': 900,
            'Others': 600,
          },
          byIntegration: {
            'Slack': 500,
            'Gmail': 800,
            'Notion': 600,
            'Jira': 400,
            'Others': 700,
          },
          projected: 6000,
        },
        roi: {
          timeSaved: 2000,
          costSaved: 100000,
          bonusesPaid: 25000,
          revenue: 10000,
        },
        topWorkflows: [
          { id: '1', name: 'Email Campaign Automator', executions: 5000, successRate: 98.5 },
          { id: '2', name: 'Lead Qualification Bot', executions: 3500, successRate: 97.2 },
          { id: '3', name: 'Invoice Generator', executions: 2800, successRate: 99.1 },
          { id: '4', name: 'Support Ticket Router', executions: 2000, successRate: 96.5 },
          { id: '5', name: 'Meeting Scheduler', executions: 1500, successRate: 98.0 },
        ],
        topUsers: [
          { id: '1', name: 'John Doe', workflowsCreated: 15, bonusesEarned: 7500 },
          { id: '2', name: 'Jane Smith', workflowsCreated: 12, bonusesEarned: 6000 },
          { id: '3', name: 'Mike Johnson', workflowsCreated: 10, bonusesEarned: 5000 },
          { id: '4', name: 'Sarah Williams', workflowsCreated: 8, bonusesEarned: 4000 },
          { id: '5', name: 'Tom Brown', workflowsCreated: 7, bonusesEarned: 3500 },
        ],
        generatedAt: new Date(),
      };

      setSystemAnalytics(mockAnalytics);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  const executionTrendData = [
    { date: 'Week 1', successful: 11000, failed: 500 },
    { date: 'Week 2', successful: 11500, failed: 600 },
    { date: 'Week 3', successful: 12200, failed: 550 },
    { date: 'Week 4', successful: 12800, failed: 850 },
  ];

  const costTrendData = [
    { month: 'Jan', actual: 4200, projected: 4500 },
    { month: 'Feb', actual: 4500, projected: 4700 },
    { month: 'Mar', actual: 4800, projected: 5000 },
    { month: 'Apr', actual: 5000, projected: 5300 },
  ];

  const COLORS = ['#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6'];

  if (!systemAnalytics) return null;

  const successRate = ((systemAnalytics.executions.successful / systemAnalytics.executions.total) * 100).toFixed(1);
  const roiPercentage = (((systemAnalytics.roi.costSaved - systemAnalytics.costs.total) / systemAnalytics.costs.total) * 100).toFixed(0);

  return (
    <div className="h-full overflow-auto bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl mb-2">Advanced Analytics</h1>
          <p className="text-gray-600">
            Comprehensive insights into workflow performance, costs, and ROI
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={period} onValueChange={(v: any) => setPeriod(v)}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Last Day</SelectItem>
              <SelectItem value="week">Last Week</SelectItem>
              <SelectItem value="month">Last Month</SelectItem>
              <SelectItem value="year">Last Year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchAnalytics}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button>
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Total Executions</span>
            <Activity className="h-5 w-5 text-purple-500" />
          </div>
          <div className="text-3xl mb-1">
            {systemAnalytics.executions.total.toLocaleString()}
          </div>
          <div className="flex items-center gap-1 text-sm">
            <TrendingUp className="h-4 w-4 text-green-500" />
            <span className="text-green-500">+12.5%</span>
            <span className="text-gray-500">vs last period</span>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Success Rate</span>
            <CheckCircle className="h-5 w-5 text-green-500" />
          </div>
          <div className="text-3xl mb-1">{successRate}%</div>
          <div className="flex items-center gap-1 text-sm">
            <span className="text-gray-600">
              {systemAnalytics.executions.successful.toLocaleString()} successful
            </span>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Total Cost</span>
            <DollarSign className="h-5 w-5 text-orange-500" />
          </div>
          <div className="text-3xl mb-1">
            ${systemAnalytics.costs.total.toLocaleString()}
          </div>
          <div className="flex items-center gap-1 text-sm">
            <span className="text-gray-600">
              Projected: ${systemAnalytics.costs.projected.toLocaleString()}
            </span>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">ROI</span>
            <TrendingUp className="h-5 w-5 text-blue-500" />
          </div>
          <div className="text-3xl mb-1 text-green-600">+{roiPercentage}%</div>
          <div className="flex items-center gap-1 text-sm">
            <span className="text-gray-600">
              ${systemAnalytics.roi.costSaved.toLocaleString()} saved
            </span>
          </div>
        </Card>
      </div>

      {/* Charts */}
      <Tabs defaultValue="performance" className="w-full">
        <TabsList>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="costs">Costs</TabsTrigger>
          <TabsTrigger value="roi">ROI Analysis</TabsTrigger>
          <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
        </TabsList>

        <TabsContent value="performance" className="mt-6 space-y-6">
          {/* Execution Trend */}
          <Card className="p-6">
            <h3 className="text-lg mb-4">Execution Trend</h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={executionTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="successful"
                  stackId="1"
                  stroke="#10b981"
                  fill="#10b981"
                  fillOpacity={0.6}
                />
                <Area
                  type="monotone"
                  dataKey="failed"
                  stackId="1"
                  stroke="#ef4444"
                  fill="#ef4444"
                  fillOpacity={0.6}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Card>

          {/* Top Workflows */}
          <Card className="p-6">
            <h3 className="text-lg mb-4">Top Performing Workflows</h3>
            <div className="space-y-4">
              {systemAnalytics.topWorkflows.map((workflow, index) => (
                <div key={workflow.id} className="flex items-center gap-4">
                  <div className="flex items-center justify-center h-8 w-8 rounded-full bg-purple-100 text-purple-700">
                    #{index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span>{workflow.name}</span>
                      <span className="text-sm text-gray-600">
                        {workflow.executions.toLocaleString()} executions
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-green-500 h-2 rounded-full"
                          style={{ width: `${workflow.successRate}%` }}
                        />
                      </div>
                      <span className="text-sm text-green-600">{workflow.successRate}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="costs" className="mt-6 space-y-6">
          {/* Cost Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="text-lg mb-4">Cost by Workflow</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={Object.entries(systemAnalytics.costs.byWorkflow).map(([name, value]) => ({
                      name,
                      value,
                    }))}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {Object.keys(systemAnalytics.costs.byWorkflow).map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg mb-4">Cost by Integration</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={Object.entries(systemAnalytics.costs.byIntegration).map(([name, value]) => ({
                    name,
                    cost: value,
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="cost" fill="#8b5cf6" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* Cost Trend */}
          <Card className="p-6">
            <h3 className="text-lg mb-4">Cost Trend & Projection</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={costTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="actual" stroke="#8b5cf6" strokeWidth={2} />
                <Line
                  type="monotone"
                  dataKey="projected"
                  stroke="#ec4899"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </TabsContent>

        <TabsContent value="roi" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="text-lg mb-4">ROI Breakdown</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-600">Time Saved</span>
                    <span className="text-lg">{systemAnalytics.roi.timeSaved.toLocaleString()} hours</span>
                  </div>
                  <p className="text-sm text-gray-500">Equivalent to 250 work days</p>
                </div>
                <div className="border-t pt-4">
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-600">Cost Saved</span>
                    <span className="text-lg text-green-600">
                      ${systemAnalytics.roi.costSaved.toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">Based on $50/hour labor cost</p>
                </div>
                <div className="border-t pt-4">
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-600">Bonuses Paid</span>
                    <span className="text-lg text-purple-600">
                      ${systemAnalytics.roi.bonusesPaid.toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">Employee workflow incentives</p>
                </div>
                <div className="border-t pt-4">
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-600">Marketplace Revenue</span>
                    <span className="text-lg text-blue-600">
                      ${systemAnalytics.roi.revenue.toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">From workflow sales</p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg mb-4">Net Impact</h3>
              <div className="space-y-6">
                <div className="text-center py-8 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg">
                  <div className="text-sm text-gray-600 mb-2">Total Savings</div>
                  <div className="text-4xl text-green-600 mb-2">
                    ${(systemAnalytics.roi.costSaved - systemAnalytics.costs.total).toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600">
                    {roiPercentage}% ROI over {period}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Investment (Costs)</span>
                    <span>${systemAnalytics.costs.total.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Return (Savings)</span>
                    <span className="text-green-600">
                      ${systemAnalytics.roi.costSaved.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm border-t pt-2">
                    <span>Net Benefit</span>
                    <span className="text-green-600">
                      ${(systemAnalytics.roi.costSaved - systemAnalytics.costs.total).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="leaderboard" className="mt-6">
          <Card className="p-6">
            <h3 className="text-lg mb-4">Top Contributors</h3>
            <div className="space-y-4">
              {systemAnalytics.topUsers.map((user, index) => (
                <div
                  key={user.id}
                  className="flex items-center gap-4 p-4 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div
                    className={`flex items-center justify-center h-10 w-10 rounded-full ${
                      index === 0
                        ? 'bg-yellow-100 text-yellow-700'
                        : index === 1
                        ? 'bg-gray-100 text-gray-700'
                        : index === 2
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-purple-100 text-purple-700'
                    }`}
                  >
                    #{index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span>{user.name}</span>
                      <Badge variant="secondary">${user.bonusesEarned.toLocaleString()} earned</Badge>
                    </div>
                    <div className="text-sm text-gray-600">
                      {user.workflowsCreated} workflows created
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};